import { memo } from 'react';
import { Focusable } from 'react-aria-components';

import { merge } from '@/components';
import { Flex, ProgressBar, Text, Tooltip, TooltipTrigger } from '@/components/core';
import { DivProps, OmitChildren } from '@/utils';

import { useValueVariant } from './useValueVariant';

export type BarCellProps = OmitChildren<DivProps> & {
  value: number;
  lowerIsBetter?: boolean;
  tooltip?: string;
};

const IBarCell = ({ value, lowerIsBetter, tooltip, className, ...rest }: BarCellProps) => {
  const valueVariant = useValueVariant(value, lowerIsBetter);

  return (
    <TooltipTrigger>
      <Tooltip className="w-48" variant="primary">
        <Text slot="description">{tooltip}</Text>
      </Tooltip>

      <Focusable>
        <Flex
          className={merge('w-fit cursor-default items-center gap-1 rounded-sm px-1.5 shadow-xs', className)}
          variant="secondary"
          role="button"
          {...rest}
        >
          <ProgressBar
            className="w-16"
            variant={valueVariant}
            value={value * 100}
            maxValue={100}
            barStyle="striped-tight"
            props={{ barBackground: { className: 'h-1.5' }, barOverlay: { className: 'h-1.5' } }}
          />
          <Text size="xs">{(value * 100).toFixed(1)}%</Text>
        </Flex>
      </Focusable>
    </TooltipTrigger>
  );
};

export const BarCell = memo(IBarCell) as typeof IBarCell;
